import map from "lodash/map";
import { DetailsPanelType } from "PFApp/booking/components/details_panel";
import { useValueRenderer } from "PFApp/components/activity_commons/use_property_renderer";
import ProfileCell from "PFApp/components/profile_cell/profile_cell";
import { TemplateKey } from "PFApp/constants/templates";
import { useCanViewParticipants } from "PFApp/hooks/activity_commons/participants";
import { BookingCategoryCell } from "PFApp/workflow/parts/activities_table/cells/booking_category_cell";
import { Icon } from "PFComponents/icon";
import { RoleStatusBadge } from "PFComponents/role/role_status_badge";
import { daysFromNowFormat } from "PFCore/helpers/date";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useDateFormatter } from "PFCore/hooks";
import { RollforwardInfo, TableColumn } from "PFTypes";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

import { PriorityBadge } from "../priority_badge";
import { ActivityNameCell } from "./cells";
import { RollforwardCell } from "./cells/roll_forward_cell";
import { getEngagementsCellRenderers } from "./engagements_cell_renderers";
import { getRolesCellRenderers } from "./roles_cell_renderers";

export const useCellFactory = (type: TemplateKey.Role | TemplateKey.Engagement | TemplateKey.AuditRole) => {
  const { t } = useTranslation(["core", "workflow"]);

  const { search } = useLocation();
  const { getCustomTypeByName } = useCustomTypes();
  const { formatDate, formatDateTime } = useDateFormatter();
  const { checkboxes: stringifyCheckboxesField } = useValueRenderer();
  const { canViewParticipants } = useCanViewParticipants();

  const cellRenderers = {
    // Activity Rows
    "name": ({ row }) => <ActivityNameCell item={row.original.activity} />,
    "id": ({ getValue }) => {
      const id = getValue();
      return (
        <Link
          to={{
            pathname: `/activities/${id}`,
            search
          }}
          state={{ utmSource: "workflow" }}
        >
          {id}
        </Link>
      );
    },
    "profile": ({ getValue }) => {
      const item = getValue();
      return item && <ProfileCell profile={item} />;
    },
    "principalOwner": ({ getValue, row }) => {
      const item = getValue();
      const canSeeOwners = canViewParticipants(row.original.activity);
      return item && canSeeOwners && <ProfileCell profile={item} />;
    },
    "custom_field": ({ getValue, column }) => {
      const type = getCustomTypeByName(column.id);
      const item = getValue();
      if (!item) {
        return null;
      }
      if (type?.value_type === "boolean") {
        const checked = item[0].value === "true";
        return (
          item && (
            <Icon
              name={checked ? "check" : "cross"}
              size="lg"
              color={checked ? "palettePrimaryAction" : "paletteRed0"}
            />
          )
        );
      }
      return Array.isArray(item) ? map(item, "text").join(", ") : item;
    },
    "noOfDays": ({ row }) => {
      const duration = row.original.activity.number_of_days;
      return duration && t("workflow:parts.day", { count: row.original.activity.number_of_days });
    },
    "date": ({ getValue }) => getValue() && formatDate(getValue()),
    "date-time": ({ getValue }) => getValue() && formatDateTime(getValue()),
    "toggle": ({ getValue }) => (
      <Icon
        name={getValue() ? "check" : "cross"}
        size="lg"
        color={getValue() ? "palettePrimaryAction" : "paletteRed0"}
      />
    ),
    "post_until": ({ getValue }) => formatDate(getValue()),
    "priority": ({ getValue }) => {
      const priority = getValue()?.[0];
      return priority && <PriorityBadge level={priority.text.toLowerCase()} />;
    },
    "status": ({ row }) => <RoleStatusBadge state={row.original.activity.state} />,
    "assignee": ({ getValue }) => {
      const item = getValue();
      return item && <ProfileCell profile={item.profile} qaId={`WorkflowPageRowProfileLink.${item.id}`} />;
    },
    "due": ({ row }) => {
      const item = row.original.activity;
      return item.state === "complete" ? "-" : daysFromNowFormat(item.post_until);
    },
    "checkboxes": ({ getValue }) => {
      const value = getValue();

      return value ? stringifyCheckboxesField({ value }) : "";
    },
    "booking_category_id": ({ getValue }) => {
      const id = getValue();
      return <BookingCategoryCell bookingCategoryId={id} />;
    },
    "rollforward_source": (info) => {
      const { row, column } = info;

      const type = column.id === "source_engagement" ? DetailsPanelType.Engagement : DetailsPanelType.Role;
      const rollforwardInfo: RollforwardInfo | undefined = row.original.activity?.rollforward_info;

      const activities = rollforwardInfo?.source ? [rollforwardInfo.source] : [];

      return <RollforwardCell activities={activities} type={type} mode="link" />;
    },
    "rollforward_targets": (info) => {
      const { row, column } = info;

      const type = column.id === "source_engagement" ? DetailsPanelType.Engagement : DetailsPanelType.Role;
      const rollforwardInfo: RollforwardInfo | undefined = row.original.activity?.rollforward_info;

      const activities = rollforwardInfo?.targets ?? [];

      return <RollforwardCell activities={activities} type={type} mode="link" />;
    },
    // Engagement Rows
    ...(type === TemplateKey.Engagement && getEngagementsCellRenderers()),
    // Roles Rows
    ...([TemplateKey.Role, TemplateKey.AuditRole].includes(type) && getRolesCellRenderers())
  };

  const defaultRenderer = ({ getValue }) => getValue() && String(getValue());

  return (column: TableColumn) =>
    cellRenderers[column.name] || cellRenderers[column.type!] || defaultRenderer;
};
